import { api, BatchUploadResponse } from "./api"

export interface UploadRecordingOptions {
  evalSuiteId?: string
  agentVersion?: string
  agentName?: string
}

/**
 * Upload a single recording Blob to the backend using the same API used by UploadTab.
 * Returns the backend response from api.uploadBatch.
 */
export async function uploadRecording(
  blob: Blob,
  options: UploadRecordingOptions = {}
): Promise<BatchUploadResponse> {
  const { evalSuiteId, agentVersion, agentName } = options
  // Try to convert the blob to MP3 if possible in-browser. If conversion isn't supported,
  // fall back to uploading the original blob.
  const convertBlobToMp3 = async (input: Blob): Promise<Blob> => {
    // Prefer explicit MP3 mime types supported by MediaRecorder
    const supportedTypes = ["audio/mpeg", "audio/mp3"]
    const mimeType = supportedTypes.find((t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) || ''

    if (!mimeType) {
      // Browser doesn't support recording MP3 via MediaRecorder — fall back
      return input
    }

    return new Promise<Blob>((resolve, reject) => {
      const audioURL = URL.createObjectURL(input)
      const audio = new Audio(audioURL)
      audio.crossOrigin = 'anonymous'

      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
      const source = ctx.createMediaElementSource(audio)
      const dest = ctx.createMediaStreamDestination()
      source.connect(dest)
      // Do not route to speakers to avoid playing audio out loud if not desired
      // but keep it connected to allow proper decoding in some browsers
      try { source.connect(ctx.destination) } catch (e) { /* ignore */ }

      const recorder = new MediaRecorder(dest.stream, { mimeType })
      const chunks: BlobPart[] = []
      let timeoutId: number | undefined

      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunks.push(ev.data)
      }
      recorder.onerror = (ev) => {
        cleanup()
        reject(ev)
      }
      recorder.onstop = () => {
        cleanup()
        const out = new Blob(chunks, { type: mimeType })
        resolve(out)
      }

      const cleanup = () => {
        try { audio.pause() } catch (e) {}
        try { audio.src = '' } catch (e) {}
        try { URL.revokeObjectURL(audioURL) } catch (e) {}
        try { source.disconnect() } catch (e) {}
        try { dest.disconnect() } catch (e) {}
        try { ctx.close() } catch (e) {}
        if (timeoutId) window.clearTimeout(timeoutId)
      }

      // Safety: stop after a reasonable timeout in case audio.onended doesn't fire
      const maxDurationMs = 60_000 // 60s

      audio.addEventListener('error', (e) => {
        cleanup()
        resolve(input) // fallback
      })

      audio.addEventListener('ended', () => {
        try { if (recorder.state !== 'inactive') recorder.stop() } catch (e) {}
      })

      recorder.start()
      // Start playback (user gesture required — this function is called from a click handler)
      audio.play().catch((err) => {
        // If playback fails, stop and fallback
        try { if (recorder.state !== 'inactive') recorder.stop() } catch (e) {}
        cleanup()
        resolve(input)
      })

      timeoutId = window.setTimeout(() => {
        try { if (recorder.state !== 'inactive') recorder.stop() } catch (e) {}
      }, maxDurationMs)
    })
  }

  const mp3Blob = await convertBlobToMp3(blob)

  const fileName = `${agentName || evalSuiteId || 'preview'}-recording.${mp3Blob.type.includes('mpeg') || mp3Blob.type.includes('mp3') ? 'mp3' : 'webm'}`
  const file = new File([mp3Blob], fileName, { type: mp3Blob.type || 'audio/mpeg' })

  const finalEvalSuiteId = evalSuiteId || 'Web Agent'
  const finalAgentVersion = `${agentName || 'agent'}-preview-${Date.now()}`

  // Reuse api.uploadBatch which expects (evalSuiteId, agentVersion, File[])
  const res = await api.uploadBatch(finalEvalSuiteId, finalAgentVersion, [file])
  return res
}
